import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/page';
import { PageLoader } from '@/components/ui/loader';
import { Select } from '@/components/ui/misc';
import { usePipelines } from '@/features/crm/api';
import { LEAD_SOURCE_LABELS } from '@/features/crm/labels';
import { apiGet } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

type Analytics = {
  pipeline: { id: string; name: string } | null;
  summary: {
    won: number;
    lost: number;
    winRate: number | null;
    wonValue: number;
    averageDealSize: number | null;
    averageCycleDays: number | null;
    openDeals: number;
    openValue: number;
    weightedValue: number;
    dealsCreated: number;
  };
  funnel: { stageId: string; name: string; colour: string; count: number; fromPrevious: number | null; fromStart: number | null }[];
  lostReasons: { reason: string; count: number }[];
  byOwner: { ownerId: string | null; name: string; won: number; wonValue: number; lost: number; winRate: number | null; open: number; openValue: number }[];
  leadSources: { source: string; leads: number; converted: number; conversionRate: number }[];
};

const aed = (value: number | null | undefined) => (value === null || value === undefined ? '--' : `AED ${value.toLocaleString('en-AE', { maximumFractionDigits: 0 })}`);

/** Reporting → Sales analytics: win rate, cycle, funnel, losses, team. */
export function SalesAnalyticsPage() {
  const t = useT();
  const [days, setDays] = useState(90);
  const [pipelineId, setPipelineId] = useState('');
  const { data: pipelines = [] } = usePipelines();
  const { data, isLoading } = useQuery({
    queryKey: ['sales-analytics', days, pipelineId],
    queryFn: () => apiGet<Analytics>('/reports/sales-analytics', { params: { days, ...(pipelineId ? { pipelineId } : {}) } }),
  });
  const top = Math.max(1, data?.funnel[0]?.count ?? 1);
  const maxLost = Math.max(1, ...(data?.lostReasons.map((row) => row.count) ?? [1]));
  return (
    <div>
      <PageHeader
        title="Sales analytics"
        description="Win rate, deal size, how long deals take, where they drop out and why — for the deals you can see."
        actions={
          <>
            <Select className="h-9 w-auto" value={pipelineId} onChange={(event) => setPipelineId(event.target.value)}>
              <option value="">{t('Default pipeline')}</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </Select>
            <Select className="h-9 w-auto" value={days} onChange={(event) => setDays(Number(event.target.value))}>
              {[30, 90, 180, 365].map((value) => (
                <option key={value} value={value}>
                  {t('Last {days} days', { days: value })}
                </option>
              ))}
            </Select>
          </>
        }
      />
      {isLoading || !data ? (
        <PageLoader />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Win rate', data.summary.winRate === null ? '--' : `${data.summary.winRate}%`, `${data.summary.won} ${t('won')} · ${data.summary.lost} ${t('lost')}`],
              ['Won value', aed(data.summary.wonValue), `${t('Average deal')} ${aed(data.summary.averageDealSize)}`],
              ['Sales cycle', data.summary.averageCycleDays === null ? '--' : `${data.summary.averageCycleDays} ${t('days')}`, t('From created to won, on average')],
              ['Open pipeline', aed(data.summary.openValue), `${data.summary.openDeals} ${t('deals')} · ${aed(data.summary.weightedValue)} ${t('weighted')}`],
            ].map(([label, value, hint]) => (
              <div key={label} className="rounded-lg border bg-card p-4">
                <p className="text-sm text-muted-foreground">{t(label)}</p>
                <p className="mt-1 text-2xl font-bold">{value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div className="rounded-lg border bg-card p-5">
              <p className="font-bold">{t('Stage funnel')}</p>
              <p className="text-xs text-muted-foreground">
                {t('{count} deals created in the period, and how many reached each stage', { count: data.summary.dealsCreated })}
              </p>
              <div className="mt-4 space-y-2">
                {data.funnel.map((step) => (
                  <div key={step.stageId} className="flex items-center gap-3">
                    <span className="w-36 shrink-0 truncate text-sm font-semibold">{step.name}</span>
                    <div className="h-7 flex-1 rounded bg-muted">
                      <div className="flex h-full items-center rounded px-2 text-xs font-bold text-white" style={{ width: `${Math.max(4, (step.count / top) * 100)}%`, backgroundColor: step.colour }}>
                        {step.count}
                      </div>
                    </div>
                    <span className="w-16 shrink-0 text-end text-xs text-muted-foreground">{step.fromPrevious === null ? '' : `${step.fromPrevious}% →`}</span>
                  </div>
                ))}
                {data.funnel.length === 0 && <p className="text-sm text-muted-foreground">{t('No pipeline yet.')}</p>}
              </div>
            </div>
            <div className="rounded-lg border bg-card p-5">
              <p className="font-bold">{t('Why deals are lost')}</p>
              <div className="mt-4 space-y-2">
                {data.lostReasons.length === 0 && <p className="text-sm text-muted-foreground">{t('No lost deals in this period.')}</p>}
                {data.lostReasons.map((row) => (
                  <div key={row.reason}>
                    <div className="flex justify-between text-sm">
                      <span>{row.reason}</span>
                      <span className="font-semibold">{row.count}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded bg-muted">
                      <div className="h-full rounded bg-destructive/70" style={{ width: `${(row.count / maxLost) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border bg-card">
            <p className="border-b px-5 py-3 font-bold">{t('By salesperson')}</p>
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-[12px] font-bold text-foreground/80">
                <tr>
                  {['Owner', 'Won', 'Won value', 'Lost', 'Win rate', 'Open', 'Open value'].map((head) => (
                    <th key={head} className={cn('px-4 py-2.5', head === 'Owner' ? 'text-start' : 'text-end')}>
                      {t(head)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.byOwner.map((row) => (
                  <tr key={row.ownerId ?? 'none'} className="tabular border-t">
                    <td className="px-4 py-2.5 font-semibold">{row.name}</td>
                    <td className="px-4 py-2.5 text-end">{row.won}</td>
                    <td className="px-4 py-2.5 text-end">{aed(row.wonValue)}</td>
                    <td className="px-4 py-2.5 text-end">{row.lost}</td>
                    <td className="px-4 py-2.5 text-end">{row.winRate === null ? '--' : `${row.winRate}%`}</td>
                    <td className="px-4 py-2.5 text-end">{row.open}</td>
                    <td className="px-4 py-2.5 text-end">{aed(row.openValue)}</td>
                  </tr>
                ))}
                {data.byOwner.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                      {t('No deals yet.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="overflow-hidden rounded-lg border bg-card">
            <p className="border-b px-5 py-3 font-bold">{t('Leads into deals, by source')}</p>
            <table className="w-full text-sm">
              <tbody>
                {data.leadSources.map((row) => (
                  <tr key={row.source} className="tabular border-t first:border-0">
                    <td className="px-4 py-2.5 font-semibold">{LEAD_SOURCE_LABELS[row.source as keyof typeof LEAD_SOURCE_LABELS] ?? row.source}</td>
                    <td className="px-4 py-2.5 text-end">
                      {row.leads} {t('leads')}
                    </td>
                    <td className="px-4 py-2.5 text-end">
                      {row.converted} {t('converted')}
                    </td>
                    <td className="w-40 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded bg-muted">
                          <div className="h-full rounded bg-primary" style={{ width: `${row.conversionRate}%` }} />
                        </div>
                        <span className="w-9 text-end text-xs font-semibold">{row.conversionRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.leadSources.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-muted-foreground">{t('No leads in this period.')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
